import React, { useRef, useState } from "react";
import { Send, Loader2, MessageSquare, Sparkles, Image as ImageIcon, Mic, Film, ArrowUpRight } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { AI_MODELS } from "@/config/site.config";

const iconFor = (id) => ({ image: ImageIcon, voice: Mic, video: Film }[id] || Sparkles);

export default function AIMarketplace() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Welcome to AIBoxHub Marketplace. Try me — I'm Claude, your premium AI concierge." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setLoading(true);

    const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let buffer = "";
    try {
      const response = await fetch(`${apiBase}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let partial = "";

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        if (!value) continue;
        partial += decoder.decode(value, { stream: true });

        // Split SSE frames by "\n\n"
        let idx;
        while ((idx = partial.indexOf("\n\n")) !== -1) {
          const frame = partial.slice(0, idx);
          partial = partial.slice(idx + 2);
          const line = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }

          if (ev.type === "session") {
            if (!sessionRef.current) sessionRef.current = ev.session_id;
          } else if (ev.type === "delta") {
            buffer += ev.text || "";
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", text: buffer };
              return next;
            });
            setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 0);
          } else if (ev.type === "error") {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", text: `⚠️ ${ev.error}` };
              return next;
            });
          }
          // type === "done" → loop exits when stream closes
        }
      }
    } catch (e) {
      // Fall back to non-streaming endpoint if SSE fails
      try {
        const { data } = await api.post("/ai/chat", { message: text, session_id: sessionRef.current });
        if (!sessionRef.current) sessionRef.current = data.session_id;
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", text: data.reply };
          return next;
        });
      } catch (e2) {
        const err = formatError(e2.response?.data?.detail) || e2.message || e.message;
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", text: `⚠️ ${err}` };
          return next;
        });
      }
    } finally {
      setLoading(false);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
    }
  };

  return (
    <section id="marketplace" className="section" data-testid="marketplace-section">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <div className="eyebrow mb-4">AI Marketplace</div>
            <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight max-w-3xl leading-[1.05]">
              One workspace. <span className="gold-text italic">Every model.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/55 text-sm leading-relaxed">
            Subscribe once. Access ChatGPT, Claude, Gemini, Grok, Llama and more — alongside image,
            voice and video AI — under a single premium plan.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Models grid */}
          <div id="chat-claude" className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-4">
            {AI_MODELS.map((m) => {
              const Icon = iconFor(m.id);
              const external = m.link?.startsWith("http");
              return (
                <a
                  key={m.id}
                  href={m.link || "#"}
                  {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="glass-card p-5 group relative block"
                  data-testid={`model-card-${m.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="h-9 w-9 rounded-lg border border-white/10 bg-white/[0.03] flex items-center justify-center">
                      <Icon className="h-4 w-4 text-gold-300" strokeWidth={1.5} />
                    </div>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
                      m.tier === "Free" ? "text-emerald-300 border border-emerald-500/30" : "text-gold-300 border border-gold-500/30"
                    }`}>{m.tier}</span>
                  </div>
                  <div className="mt-4 font-serif text-lg text-white">{m.name}</div>
                  <div className="text-xs text-white/45 mt-0.5">{m.vendor}</div>
                  <div className="mt-3 flex items-center justify-between">
                    {m.live ? (
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live demo
                      </span>
                    ) : <span />}
                    <ArrowUpRight className="h-3.5 w-3.5 text-white/40 group-hover:text-gold-300 transition-colors" />
                  </div>
                </a>
              );
            })}
          </div>

          {/* Live Claude chat */}
          <div className="lg:col-span-5">
            <div className="glass-card p-0 overflow-hidden flex flex-col h-[520px]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-gold-300" />
                  <span className="font-serif text-base text-white">Claude — Live Demo</span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-300">Sonnet 4.6</span>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gold-500/15 text-white border border-gold-500/30"
                        : "bg-white/[0.04] text-white/85 border border-white/8"
                    }`}>
                      {m.text}
                    </div>
                  </div>
                ))}
                {loading && messages[messages.length - 1]?.text === "" && (
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Claude is thinking…
                  </div>
                )}
              </div>
              <div className="border-t border-white/8 p-3 flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  className="flex-1 bg-transparent text-sm text-white placeholder-white/30 outline-none px-3 py-2"
                  placeholder="Ask anything about AIBoxHub…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  disabled={loading}
                />
                <button
                  data-testid="chat-send-btn"
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className="pill pill-gold !px-4 !py-2 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
